import React from 'react';
import { FileText, ChevronDown, Check, AlertTriangle } from 'lucide-react';

export interface ContractOption {
  id: string;
  name: string;
  state: string;
  status: string;
  version?: string;
  effective_date?: string;
}

export interface ContractSelectorProps {
  label: string;
  contracts: ContractOption[];
  selectedContractId: string;
  onSelect: (contractId: string) => void;
  excludeContractId?: string; // Exclude this contract from the list (e.g., source when selecting target)
  placeholder?: string;
  testId?: string;
  disabled?: boolean;
  error?: string;
}

export const ContractSelector: React.FC<ContractSelectorProps> = ({
  label,
  contracts,
  selectedContractId,
  onSelect,
  excludeContractId,
  placeholder = 'Select a contract',
  testId = 'contract-selector',
  disabled = false,
  error,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Filter contracts: only processed contracts (structured, embedded, linked, ready)
  const processedStatuses = ['structured', 'embedded', 'linked', 'ready'];
  const availableContracts = contracts.filter(
    (contract) =>
      processedStatuses.includes(contract.status) &&
      contract.id !== excludeContractId
  );

  const selectedContract = contracts.find((c) => c.id === selectedContractId);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (contractId: string) => {
    onSelect(contractId);
    setIsOpen(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ready':
        return 'bg-success-100 text-success-700';
      case 'linked':
        return 'bg-primary-100 text-primary-700';
      case 'embedded':
        return 'bg-secondary-100 text-secondary-700';
      case 'structured':
        return 'bg-warning-100 text-warning-700';
      default:
        return 'bg-neutral-100 text-neutral-700';
    }
  };

  return (
    <div className="form-field" ref={dropdownRef} data-testid={testId}>
      <label className="label" data-testid={`${testId}-label`}>
        {label}
      </label>
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`
            dropdown-trigger w-full py-3 px-4 text-left justify-between
            ${disabled ? 'bg-neutral-50 cursor-not-allowed opacity-50' : ''}
            ${error ? 'border-danger-500 focus:ring-danger-500' : ''}
          `}
          data-testid={`${testId}-button`}
        >
          {selectedContract ? (
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center min-w-0">
                <FileText className="h-5 w-5 text-neutral-400 mr-3 flex-shrink-0" />
                <div className="truncate">
                  <span className="block font-medium text-neutral-900 truncate">
                    {selectedContract.name}
                  </span>
                  <span className="block text-xs text-neutral-500">
                    {selectedContract.state}
                    {selectedContract.version && ` • v${selectedContract.version}`}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`px-2 py-0.5 text-xs rounded-full font-medium ${getStatusColor(
                    selectedContract.status
                  )}`}
                >
                  {selectedContract.status}
                </span>
                <ChevronDown className={`h-5 w-5 text-neutral-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between w-full text-neutral-500">
              <span>{placeholder}</span>
              <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </div>
          )}
        </button>

        {/* Dropdown list */}
        <div
          className={`dropdown-menu ${isOpen ? 'open' : ''}`}
          data-testid={`${testId}-dropdown`}
        >
          {availableContracts.length === 0 ? (
            <div className="py-6 px-4 text-center" data-testid={`${testId}-empty`}>
              <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-warning-500" />
              <p className="text-sm font-medium text-neutral-700">No processed contracts available</p>
              <p className="text-xs text-neutral-500 mt-1">Contracts must be processed before comparison</p>
            </div>
          ) : (
            availableContracts.map((contract) => (
              <button
                key={contract.id}
                type="button"
                onClick={() => handleSelect(contract.id)}
                className={`dropdown-item ${contract.id === selectedContractId ? 'dropdown-item-active' : ''}`}
                data-testid={`${testId}-option-${contract.id}`}
              >
                <div className="flex items-center min-w-0 flex-1">
                  <FileText className="h-5 w-5 text-neutral-400 mr-3 flex-shrink-0" />
                  <div className="truncate">
                    <span className="block font-medium text-neutral-900 truncate">
                      {contract.name}
                    </span>
                    <span className="block text-xs text-neutral-500">
                      {contract.state}
                      {contract.version && ` • v${contract.version}`}
                      {contract.effective_date && ` • Effective: ${contract.effective_date}`}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-3">
                  <span
                    className={`px-2 py-0.5 text-xs rounded-full font-medium ${getStatusColor(
                      contract.status
                    )}`}
                  >
                    {contract.status}
                  </span>
                  {contract.id === selectedContractId && (
                    <Check className="h-5 w-5 text-primary-600" />
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </div>
      {error && (
        <p className="input-error-message" data-testid={`${testId}-error`}>
          {error}
        </p>
      )}
    </div>
  );
};

export default ContractSelector;
